import React from "react";
const THEMES = {
  saponeria: { primary: "#00A651", secondary: "#39B54A", accent: "#FF1493", light: "#E8F5E9" },
  acquasapone: { primary: "#0066CC", secondary: "#004499", accent: "#FF1493", light: "#E3F0FF" },
  default: { primary: "#7c3aed", secondary: "#5b21b6", accent: "#f59e0b", light: "#f5f3ff" },
};
function getTheme(n) {
  const s = (n||"").toLowerCase();
  if (s.includes("saponeria")) return THEMES.saponeria;
  if (s.includes("acqua")) return THEMES.acquasapone;
  return THEMES.default;
}
function fmtEur(n) { return "\u20ac" + (n||0).toLocaleString("it-IT",{minimumFractionDigits:2,maximumFractionDigits:2}); }
function fmt(n) { return (n||0).toLocaleString("it-IT"); }
function fmtPct(a,b) { if(!b)return"0,00%"; return ((a/b)*100).toLocaleString("it-IT",{minimumFractionDigits:2,maximumFractionDigits:2})+"%"; }

function PieChart({ slices, colors, size }) {
  const sz = size||120, r = sz/2-6, cx = sz/2, cy = sz/2;
  const total = slices.reduce((s,v)=>s+v,0);
  if (!total) return null;
  let a = -Math.PI/2;
  const paths = slices.map((v,i) => {
    const da = (v/total)*2*Math.PI;
    const x1 = cx+r*Math.cos(a), y1 = cy+r*Math.sin(a);
    a += da;
    const x2 = cx+r*Math.cos(a), y2 = cy+r*Math.sin(a);
    return <path key={i} d={"M"+cx+","+cy+" L"+x1+","+y1+" A"+r+","+r+" 0 "+(da>Math.PI?1:0)+",1 "+x2+","+y2+" Z"} fill={colors[i]}/>;
  });
  return <svg width={sz} height={sz} viewBox={"0 0 "+sz+" "+sz}>{paths}</svg>;
}

export default function ReportTemplate({ client, dateFrom, dateTo, campaigns, campaignInsights, demographics, mockupImageUrl }) {
  const theme = getTheme(client?.name);
  const totalSpend  = campaigns.reduce((s,c)=>s+(campaignInsights[c.id]?.spend||0),0);
  const totalImpr   = campaigns.reduce((s,c)=>s+(campaignInsights[c.id]?.impressions||0),0);
  const totalClicks = campaigns.reduce((s,c)=>s+(campaignInsights[c.id]?.clicks||0),0);
  const ctr = fmtPct(totalClicks, totalImpr);
  const cpc = totalClicks>0 ? fmtEur(totalSpend/totalClicks) : fmtEur(0);
  const cpm = totalImpr>0  ? fmtEur(totalSpend/totalImpr*1000) : fmtEur(0);
  const top = [...campaigns].sort((a,b)=>(campaignInsights[b.id]?.spend||0)-(campaignInsights[a.id]?.spend||0));
  const today = new Date().toLocaleDateString("it-IT",{day:"2-digit",month:"long",year:"numeric"});
  const df = new Date(dateFrom).toLocaleDateString("it-IT",{day:"2-digit",month:"long",year:"numeric"});
  const dt = new Date(dateTo).toLocaleDateString("it-IT",{day:"2-digit",month:"long",year:"numeric"});
  const demo = demographics || { gender: [], age: [] };

  const SH = ({sub,title}) => (
    <div style={{display:"flex",gap:12,alignItems:"flex-end",marginBottom:20}}>
      <div style={{width:5,height:36,background:theme.primary,borderRadius:3}}/>
      <div>
        <div style={{fontSize:10,fontWeight:700,color:theme.primary,textTransform:"uppercase",letterSpacing:3}}>{sub}</div>
        <div style={{fontSize:26,fontWeight:800,color:"#1a1a2e"}}>{title}</div>
      </div>
    </div>
  );

  const kpiList = [
    {l:"Impression", v:fmt(totalImpr)},
    {l:"CPM", v:cpm},
    {l:"Clic", v:fmt(totalClicks)},
    {l:"CTR", v:ctr},
    {l:"CPC", v:cpc},
    {l:"Importo speso", v:fmtEur(totalSpend)},
  ];

  const genderData = demo.gender ? demo.gender.filter(g=>g.gender!=="UNDETERMINED") : [];
  const ageData = demo.age || [];
  const ageLabels = {AGE_RANGE_18_24:"18-24",AGE_RANGE_25_34:"25-34",AGE_RANGE_35_44:"35-44",AGE_RANGE_45_54:"45-54",AGE_RANGE_55_64:"55-64",AGE_RANGE_65_UP:"65+"};
  const genderColors = ["#5BC4F5","#F5A0C8"];
  const genderLabels = {MALE:"Maschi",FEMALE:"Femmine"};
  const totalG = genderData.reduce((s,g)=>s+g.impressions,0);
  const maxAge = ageData.length > 0 ? Math.max(...ageData.map(a=>a.clicks),1) : 1;
  const hasDemographics = genderData.length > 0 || ageData.length > 0;

  return (
    <div style={{fontFamily:"Montserrat,sans-serif",background:"#fff",minWidth:800}}>
      <link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700;800;900&display=swap" rel="stylesheet"/>
      <style>{"@media print{.no-print{display:none!important}body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}"}</style>

      {/* COVER */}
      <div style={{background:"linear-gradient(135deg,"+theme.primary+","+theme.secondary+")",padding:"60px 70px",position:"relative",overflow:"hidden",minHeight:420,display:"flex",flexDirection:"column",justifyContent:"space-between",pageBreakAfter:"always"}}>
        {[300,200,150,400,250].map((s,i)=>(
          <div key={i} style={{position:"absolute",width:s,height:s,borderRadius:"50%",border:"2px solid rgba(255,255,255,0.12)",top:[-80,50,-20,100,280][i],left:[-60,30,120,500,620][i],pointerEvents:"none"}}/>
        ))}
        <div style={{display:"flex",justifyContent:"space-between",position:"relative",zIndex:1}}>
          <div style={{display:"flex",gap:12,alignItems:"center"}}>
            <span style={{background:"rgba(255,255,255,0.2)",borderRadius:8,padding:"6px 14px",color:"#fff",fontWeight:800,fontSize:14,letterSpacing:2}}>GALLICANTUS</span>
            <span style={{color:"rgba(255,255,255,0.5)",fontSize:18}}>x</span>
            <span style={{background:"rgba(255,255,255,0.2)",borderRadius:8,padding:"6px 14px",color:"#fff",fontWeight:800,fontSize:14}}>{(client?.name||"").toUpperCase()}</span>
          </div>
          <div style={{color:"rgba(255,255,255,0.7)",fontSize:12,textAlign:"right"}}>
            <div style={{color:"#fff",fontWeight:700}}>REPORT ADV</div>
            <div>Generato il {today}</div>
          </div>
        </div>
        <div style={{position:"relative",zIndex:1}}>
          <div style={{color:"rgba(255,255,255,0.8)",fontSize:13,fontWeight:700,textTransform:"uppercase",letterSpacing:4,marginBottom:8}}>REPORT</div>
          <div style={{color:"#fff",fontSize:80,fontWeight:900,lineHeight:1,textTransform:"uppercase",letterSpacing:-2}}>ADV</div>
          <div style={{marginTop:20,color:"#fff",fontSize:20,fontWeight:700}}>{campaigns.length===1?campaigns[0].name:campaigns.length+" campagne"}</div>
          <div style={{marginTop:8,color:"rgba(255,255,255,0.8)",fontSize:15}}>
            {campaigns.length===1 && campaigns[0].start_date
              ? "Dal " + new Date(campaigns[0].start_date).toLocaleDateString("it-IT",{day:"2-digit",month:"long",year:"numeric"})
                + " al " + (campaigns[0].end_date
                  ? new Date(campaigns[0].end_date).toLocaleDateString("it-IT",{day:"2-digit",month:"long",year:"numeric"})
                  : "oggi")
              : "Periodo: " + df + " — " + dt}
          </div>
          <div style={{marginTop:4,color:"rgba(255,255,255,0.7)",fontSize:13}}>Google Ads · {campaigns.length} campagne attive</div>
        </div>
      </div>

      {/* BODY */}
      <div style={{padding:"50px 70px"}}>

        {/* SEZIONE KPI */}
        <div style={{marginBottom:50}}>
          <div style={{marginBottom:24}}>
            <div style={{fontSize:10,fontWeight:700,color:theme.primary,textTransform:"uppercase",letterSpacing:3}}>Google Ads insights</div>
            <div style={{fontSize:34,fontWeight:900,color:"#1a1a2e",letterSpacing:-1}}>Google insights</div>
            <div style={{fontSize:14,fontStyle:"italic",color:theme.accent,fontWeight:600,marginTop:2}}>
              {"Performance Overview"+(campaigns.length===1?" | "+campaigns[0].name:"")}
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"320px 1fr",gap:48,alignItems:"start"}}>

            {/* Sinistra: mockup annuncio Display Google */}
            <div>
              {/* Card annuncio stile Google Display */}
              <div style={{
                border:"1px solid #dadce0", borderRadius:8, overflow:"hidden",
                boxShadow:"0 1px 6px rgba(32,33,36,0.18)", background:"#fff",
                maxWidth:320, fontFamily:"'Google Sans',Roboto,Arial,sans-serif"
              }}>
                {/* Immagine annuncio */}
                {mockupImageUrl ? (
                  <div style={{width:"100%",paddingTop:"52.5%",position:"relative",background:"#f1f3f4",overflow:"hidden"}}>
                    <img src={mockupImageUrl} alt="ad"
                      style={{position:"absolute",top:0,left:0,width:"100%",height:"100%",objectFit:"cover",display:"block"}}/>
                  </div>
                ) : (
                  <div style={{width:"100%",paddingTop:"52.5%",position:"relative",background:"#e8eaed"}}>
                    <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",color:"#9aa0a6",fontSize:12}}>
                      Immagine non disponibile
                    </div>
                  </div>
                )}

                {/* Footer annuncio */}
                <div style={{padding:"10px 12px 12px",background:"#fff"}}>
                  {/* Riga 1: advertiser */}
                  <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:6}}>
                    <div style={{
                      width:20,height:20,borderRadius:"50%",
                      background:"linear-gradient(135deg,"+theme.primary+","+theme.secondary+")",
                      flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center"
                    }}>
                      <span style={{color:"#fff",fontSize:9,fontWeight:700}}>
                        {(client?.name||"A").charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <span style={{fontSize:12,color:"#202124",fontWeight:500,lineHeight:1}}>
                      {client?.name||"Advertiser"}
                    </span>
                    <span style={{
                      marginLeft:"auto",fontSize:9,color:"#70757a",
                      border:"1px solid #dadce0",borderRadius:2,padding:"1px 4px",flexShrink:0
                    }}>
                      Ann
                    </span>
                  </div>

                  {/* Headline */}
                  <div style={{fontSize:14,fontWeight:700,color:"#202124",lineHeight:1.3,marginBottom:4}}>
                    {campaigns[0]?.ad_headline || client?.name || ""}
                  </div>

                  {/* Descrizione */}
                  {campaigns[0]?.ad_description && (
                    <div style={{fontSize:12,color:"#5f6368",lineHeight:1.4,marginBottom:8}}>
                      {campaigns[0].ad_description}
                    </div>
                  )}

                  {/* Riga URL + CTA */}
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:4}}>
                    <div style={{fontSize:11,color:"#70757a",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",flex:1,marginRight:8}}>
                      {campaigns[0]?.ad_final_url
                        ? campaigns[0].ad_final_url.replace(/^https?:\/\//,"").split("/")[0]
                        : ""}
                    </div>
                    <div style={{
                      background:theme.primary,color:"#fff",
                      fontSize:11,fontWeight:600,padding:"6px 14px",
                      borderRadius:4,flexShrink:0,whiteSpace:"nowrap"
                    }}>
                      Scopri di più
                    </div>
                  </div>
                </div>
              </div>

              {/* Label sotto */}
              <div style={{marginTop:8,fontSize:10,color:"#9aa0a6",textAlign:"center"}}>
                Anteprima annuncio Display
              </div>
            </div>

            {/* Destra: KPI grandi */}
            <div style={{paddingTop:4}}>
              {kpiList.map((k,i)=>(
                <div key={i} style={{display:"flex",justifyContent:"space-between",
                  alignItems:"baseline",padding:"13px 0",
                  borderBottom:"2px solid "+theme.light}}>
                  <span style={{fontSize:16,fontWeight:700,color:"#1a1a2e"}}>{k.l}:</span>
                  <span style={{fontSize:30,fontWeight:900,color:theme.accent,letterSpacing:-1}}>{k.v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* TABELLA — solo se più di 1 campagna */}
        {top.length > 1 && (
          <div style={{marginBottom:40}}>
            <SH sub="Dettaglio" title="Campagne"/>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
              <thead>
                <tr style={{background:"linear-gradient(90deg,"+theme.primary+","+theme.secondary+")"}}>
                  {["Campagna","Account","Spend","Impression","Clic","CTR"].map((h,i)=>(
                    <th key={i} style={{padding:"12px 14px",color:"#fff",fontWeight:700,textAlign:i>1?"right":"left",fontSize:10,textTransform:"uppercase",letterSpacing:1}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {top.map((c,i)=>{
                  const ins=campaignInsights[c.id]||{};
                  return (
                    <tr key={c.id} style={{background:i%2===0?"#fff":theme.light,borderBottom:"1px solid "+theme.light}}>
                      <td style={{padding:"11px 14px",fontWeight:600,color:"#1a1a2e",maxWidth:250,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.name}</td>
                      <td style={{padding:"11px 14px",fontSize:11,color:"#666"}}>{c.ad_accounts?.name||"—"}</td>
                      <td style={{padding:"11px 14px",textAlign:"right",fontWeight:700,color:ins.spend>0?theme.primary:"#bbb"}}>{fmtEur(ins.spend)}</td>
                      <td style={{padding:"11px 14px",textAlign:"right",color:"#333"}}>{fmt(ins.impressions)}</td>
                      <td style={{padding:"11px 14px",textAlign:"right",color:"#333"}}>{fmt(ins.clicks)}</td>
                      <td style={{padding:"11px 14px",textAlign:"right",color:"#333"}}>{fmtPct(ins.clicks,ins.impressions)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{background:"#1a1a2e"}}>
                  <td colSpan={2} style={{padding:"12px 14px",color:"#fff",fontWeight:800,textTransform:"uppercase",fontSize:11}}>TOTALE</td>
                  <td style={{padding:"12px 14px",textAlign:"right",color:theme.accent,fontWeight:900,fontSize:15}}>{fmtEur(totalSpend)}</td>
                  <td style={{padding:"12px 14px",textAlign:"right",color:"#fff",fontWeight:700}}>{fmt(totalImpr)}</td>
                  <td style={{padding:"12px 14px",textAlign:"right",color:"#fff",fontWeight:700}}>{fmt(totalClicks)}</td>
                  <td style={{padding:"12px 14px",textAlign:"right",color:"#fff",fontWeight:700}}>{ctr}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {/* SPEND BAR — solo se più di 1 campagna */}
        {top.length > 1 && (
          <div style={{marginBottom:40}}>
            <SH sub="Distribuzione" title="Spend per Campagna"/>
            {top.map((c)=>{
              const spend=campaignInsights[c.id]?.spend||0;
              const pct=totalSpend>0?spend/totalSpend*100:0;
              return (
                <div key={c.id} style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                  <div style={{width:200,fontSize:11,color:"#555",fontWeight:500,flexShrink:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.name.length>30?c.name.slice(0,30)+"...":c.name}</div>
                  <div style={{flex:1,background:theme.light,borderRadius:20,height:24,overflow:"hidden"}}>
                    <div style={{height:"100%",borderRadius:20,background:"linear-gradient(90deg,"+theme.primary+","+theme.secondary+")",width:pct+"%",minWidth:pct>0?3:0}}/>
                  </div>
                  <div style={{width:90,textAlign:"right",fontWeight:700,color:theme.primary,fontSize:12,flexShrink:0}}>{fmtEur(spend)}</div>
                  <div style={{width:50,textAlign:"right",color:"#888",fontSize:11,flexShrink:0}}>{pct.toLocaleString("it-IT",{maximumFractionDigits:1})}%</div>
                </div>
              );
            })}
          </div>
        )}

        {/* DEMOGRAFICI */}
        {hasDemographics && (
          <div style={{marginBottom:40}}>
            <SH sub="Audience" title="Dati Demografici"/>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:24}}>
              {genderData.length > 0 && (
                <div style={{background:theme.light,borderRadius:16,padding:"24px"}}>
                  <div style={{fontSize:12,fontWeight:700,color:theme.primary,textTransform:"uppercase",letterSpacing:2,marginBottom:20}}>Genere — Impressioni</div>
                  <div style={{display:"flex",alignItems:"center",gap:24}}>
                    <PieChart slices={genderData.map(g=>g.impressions)} colors={genderColors} size={120}/>
                    <div style={{flex:1}}>
                      {genderData.map((g,i)=>(
                        <div key={g.gender} style={{marginBottom:12}}>
                          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3}}>
                            <div style={{width:12,height:12,borderRadius:"50%",background:genderColors[i]}}/>
                            <span style={{fontSize:13,fontWeight:700,color:"#1a1a2e"}}>{genderLabels[g.gender]||g.gender}</span>
                            <span style={{fontSize:12,color:"#888",marginLeft:"auto"}}>{totalG>0?((g.impressions/totalG)*100).toLocaleString("it-IT",{maximumFractionDigits:1})+"%" :""}</span>
                          </div>
                          <div style={{fontSize:11,color:"#666",paddingLeft:20}}>{fmt(g.impressions)} impr · {fmtEur(g.spend)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {ageData.length > 0 && (
                <div style={{background:theme.light,borderRadius:16,padding:"24px"}}>
                  <div style={{fontSize:12,fontWeight:700,color:theme.primary,textTransform:"uppercase",letterSpacing:2,marginBottom:20}}>Fascia d eta — Clic</div>
                  <div style={{display:"flex",flexDirection:"column",gap:10}}>
                    {ageData.map(a=>{
                      const pct=a.clicks/maxAge*100;
                      return (
                        <div key={a.age_range}>
                          <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                            <span style={{fontSize:12,fontWeight:700,color:"#1a1a2e",width:50}}>{ageLabels[a.age_range]||a.age_range}</span>
                            <span style={{fontSize:11,color:"#888"}}>{fmt(a.clicks)} clic · {fmtEur(a.spend)}</span>
                          </div>
                          <div style={{background:"rgba(0,0,0,0.08)",borderRadius:20,height:18,overflow:"hidden"}}>
                            <div style={{height:"100%",borderRadius:20,background:"linear-gradient(90deg,"+theme.primary+","+theme.secondary+")",width:pct+"%",minWidth:2}}/>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* FOOTER */}
        <div style={{borderTop:"3px solid "+theme.primary,paddingTop:16,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{fontSize:11,color:"#999"}}>Report generato da <strong>Gallicantus Reporting Platform</strong> · {today}</div>
          <div style={{fontSize:11,color:"#999"}}>{client?.name} · {dateFrom} {String.fromCharCode(8594)} {dateTo}</div>
        </div>
      </div>
    </div>
  );
}
